import { useEffect, useState } from 'react';
import {
  Users,
  Users2,
  Brain,
  Cpu,
  FileText,
  Wrench,
  CreditCard,
  Mic,
  Volume2,
  Home,
  ChevronDown,
  UserPlus,
  User,
  UserCheck,
  UserCog,
  Mail,
  BookText,
  MessageSquareText,
  ScrollText,
  PieChart,
  Globe,
  Zap,
  Shield,
  Receipt,
  Calculator,
  Bell,
  Plug,
  Tag,
  Wallet,
  Sparkles,
  Bot,
  Puzzle,
  Megaphone,
  Phone,
  PhoneOff,
  Hash,
  Settings,
  Star,
  Layers,
  Activity,
  BarChart3,
  AlertTriangle,
  Building2,
  Network,
  ArrowRightLeft,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageType } from './Dashboard';

const COLLAPSED_GROUPS_STORAGE_KEY = 'admin-sidebar:collapsed-groups';
const FAVORITE_ITEMS_STORAGE_KEY = 'admin-sidebar:favorite-items';

function loadStringSet(storageKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((entry): entry is string => typeof entry === 'string'));
  } catch {
    return new Set();
  }
}

function persistStringSet(storageKey: string, value: Set<string>): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...value]));
  } catch {
    // localStorage may be unavailable (private mode, quota) — state stays
    // in memory for the session and silently fails to persist.
  }
}

interface SidebarProps {
  currentPage: PageType;
  onPageChange: (page: PageType) => void;
  /** Admin role of the logged-in user. Drives which menu items are visible. */
  userRole?: string;
}

interface MenuItem {
  id: PageType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  /** Pages restricted to Super-Admins only. Portal-Admins see only `portalAdmin` items. */
  superAdminOnly?: boolean;
  /** Page is in the Portal Admin allowlist (overview, customers, billing, registrations, profile). */
  portalAdmin?: boolean;
}

interface MenuGroup {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Tailwind text color class for the group header (label + icon). */
  colorClass: string;
  /** Tailwind border color class used as a thin left accent on the group items. */
  accentClass: string;
  /** Tailwind text color class for the icons of items within this group. */
  iconClass: string;
  items: MenuItem[];
}

const overviewItem: MenuItem = {
  id: 'overview',
  label: 'Übersicht',
  icon: Home,
  description: 'Dashboard und Statistiken',
  portalAdmin: true,
};

const profileItem: MenuItem = {
  id: 'profile',
  label: 'Mein Profil',
  icon: User,
  description: 'Kontoeinstellungen',
  portalAdmin: true,
};

const menuGroups: MenuGroup[] = [
  {
    label: 'Monitoring',
    icon: Activity,
    colorClass: 'text-cyan-700',
    accentClass: 'border-cyan-200',
    iconClass: 'text-cyan-500',
    items: [
      {
        id: 'call-volume',
        label: 'Session-Volumen',
        icon: BarChart3,
        description: 'Anrufe & Chats je Monat',
        portalAdmin: true,
      },
    ],
  },
  {
    label: 'Kunden',
    icon: Users2,
    colorClass: 'text-blue-700',
    accentClass: 'border-blue-200',
    iconClass: 'text-blue-500',
    items: [
      {
        id: 'customers',
        label: 'Kunden',
        icon: Users,
        description: 'Kundenverwaltung',
        portalAdmin: true,
      },
      {
        id: 'pending-registrations',
        label: 'Registrierungsanfragen',
        icon: UserCheck,
        description: 'Anträge verwalten',
        portalAdmin: true,
      },
      {
        id: 'registration-profiles',
        label: 'Registrierungsprofile',
        icon: UserPlus,
        description: 'Self-Service Registrierung',
      },
    ],
  },
  {
    label: 'Tenants',
    icon: Network,
    colorClass: 'text-teal-700',
    accentClass: 'border-teal-200',
    iconClass: 'text-teal-500',
    items: [
      {
        id: 'tenants',
        label: 'Tenants (External API)',
        icon: Building2,
        description: 'Partner und Zugangsdaten',
        superAdminOnly: true,
      },
      {
        id: 'tenant-assignments',
        label: 'Kunden-Zuordnung',
        icon: ArrowRightLeft,
        description: 'Kunden Tenants zuweisen',
        superAdminOnly: true,
      },
    ],
  },
  {
    label: 'Abrechnung',
    icon: Wallet,
    colorClass: 'text-emerald-700',
    accentClass: 'border-emerald-200',
    iconClass: 'text-emerald-500',
    items: [
      {
        id: 'billing',
        label: 'Abrechnungen',
        icon: Receipt,
        description: 'Abrechnungsübersicht',
        portalAdmin: true,
      },
      {
        id: 'subscription-plans',
        label: 'Abonnement Pläne',
        icon: CreditCard,
        description: 'Tarifverwaltung',
      },
      {
        id: 'cost-overview',
        label: 'Kostenübersicht',
        icon: Calculator,
        description: 'Plattform-Kosten & Marge je Kunde',
      },
      {
        id: 'cost-settings',
        label: 'Kosten-Einstellungen',
        icon: Settings,
        description: 'USD→EUR-Faktor & RAG-Pauschalen',
      },
    ],
  },
  {
    label: 'KI & Sprache',
    icon: Sparkles,
    colorClass: 'text-violet-700',
    accentClass: 'border-violet-200',
    iconClass: 'text-violet-500',
    items: [
      {
        id: 'llm-providers',
        label: 'LLM Provider',
        icon: Brain,
        description: 'Sprachmodell-Anbieter',
      },
      {
        id: 'llm-models',
        label: 'LLM Modelle',
        icon: Cpu,
        description: 'Verfügbare Modelle',
      },
      {
        id: 'speech-to-text',
        label: 'Speech-to-Text',
        icon: Mic,
        description: 'Spracherkennung',
      },
      {
        id: 'text-to-speech',
        label: 'Text-to-Speech',
        icon: Volume2,
        description: 'Sprachsynthese',
      },
      {
        id: 'bulk-model-activation',
        label: 'Modelle aktivieren',
        icon: Layers,
        description: 'Modelle & Provider für Kunden freischalten',
      },
    ],
  },
  {
    label: 'Bot-Konfiguration',
    icon: Bot,
    colorClass: 'text-amber-700',
    accentClass: 'border-amber-200',
    iconClass: 'text-amber-500',
    items: [
      {
        id: 'prompt-templates',
        label: 'Prompt Templates',
        icon: FileText,
        description: 'Vorlage-Verwaltung',
      },
      {
        id: 'contact-reasons',
        label: 'Kontaktgründe',
        icon: MessageSquareText,
        description: 'Kontaktgründe für Templates',
      },
      {
        id: 'summary-collections',
        label: 'Summary Collections',
        icon: BookText,
        description: 'Session-Zusammenfassungen',
      },
      {
        id: 'system-guidelines',
        label: 'System-Richtlinien',
        icon: ScrollText,
        description: 'Globale Bot-Richtlinien',
      },
      {
        id: 'evaluation-criteria',
        label: 'Standard-Auswertungen',
        icon: PieChart,
        description: 'Qualitatives Reporting',
      },
      {
        id: 'filler-settings',
        label: 'Filler Einstellungen',
        icon: Zap,
        description: 'Überbrückungsphrasen',
      },
      {
        id: 'background-job-llms',
        label: 'Background Job LLMs',
        icon: Cpu,
        description: 'LLM für Hook-Content',
      },
    ],
  },
  {
    label: 'Tools & Integrationen',
    icon: Puzzle,
    colorClass: 'text-cyan-700',
    accentClass: 'border-cyan-200',
    iconClass: 'text-cyan-500',
    items: [
      {
        id: 'tool-definitions',
        label: 'Tool Definitionen',
        icon: Wrench,
        description: 'Werkzeug-Konfiguration & Auth',
      },
      {
        id: 'condition-suggestions',
        label: 'Bedingungs-Vorschläge',
        icon: Tag,
        description: 'Tool Pre-Condition Bibliothek',
      },
      {
        id: 'mcp-servers',
        label: 'MCP Servers',
        icon: Plug,
        description: 'MCP Server Verbindungen',
      },
      {
        id: 'integrations',
        label: 'Integrationen',
        icon: Plug,
        description: 'Kuratierte Integrationen',
      },
      {
        id: 'curated-notifications',
        label: 'Integration-Alerts',
        icon: Bell,
        description: 'Änderungs-Benachrichtigungen',
      },
    ],
  },
  {
    label: 'Kommunikation',
    icon: Megaphone,
    colorClass: 'text-rose-700',
    accentClass: 'border-rose-200',
    iconClass: 'text-rose-500',
    items: [
      {
        id: 'hook-providers',
        label: 'Hook Providers',
        icon: Mail,
        description: 'E-Mail & SMS Provider',
      },
      {
        id: 'webhook-templates',
        label: 'Webhook Templates',
        icon: Globe,
        description: 'Webhook-Vorlagen verwalten',
      },
      {
        id: 'notification-templates',
        label: 'Benachrichtigungsvorlagen',
        icon: Mail,
        description: 'E-Mail-Vorlagen verwalten',
      },
      {
        id: 'system-events',
        label: 'System Events',
        icon: Bell,
        description: 'Benachrichtigungsregeln',
      },
    ],
  },
  {
    label: 'Telefonie',
    icon: Phone,
    colorClass: 'text-indigo-700',
    accentClass: 'border-indigo-200',
    iconClass: 'text-indigo-500',
    items: [
      {
        id: 'blocked-phone-numbers',
        label: 'Gesperrte Nummern',
        icon: PhoneOff,
        description: 'Outcall-Sperrliste',
        superAdminOnly: true,
      },
      {
        id: 'cid-pool',
        label: 'Rufnummern-Pool',
        icon: Hash,
        description: 'Reservierte CID-Nummern verwalten',
        superAdminOnly: true,
      },
    ],
  },
  {
    label: 'System',
    icon: Settings,
    colorClass: 'text-slate-700',
    accentClass: 'border-slate-200',
    iconClass: 'text-slate-500',
    items: [
      {
        id: 'system-event-log',
        label: 'System Event Log',
        icon: Activity,
        description: 'Audit-Verlauf aller Events',
        superAdminOnly: true,
      },
      {
        id: 'rate-limit-settings',
        label: 'Rate Limiting',
        icon: Shield,
        description: 'Spam-Schutz Einstellungen',
      },
      {
        id: 'rate-limit-logs',
        label: 'Rate Limit Logs',
        icon: Shield,
        description: 'Verstöße und Sperren',
      },
      {
        id: 'error-events',
        label: 'Error Events',
        icon: AlertTriangle,
        description: 'Fehler aller Services',
        superAdminOnly: true,
      },
      {
        id: 'admin-users',
        label: 'Admin Users',
        icon: UserCog,
        description: 'Callbert-Admins verwalten',
        superAdminOnly: true,
      },
    ],
  },
];

function isVisible(item: MenuItem, userRole?: string): boolean {
  if (item.superAdminOnly) {
    return userRole === 'super_admin';
  }
  if (userRole === 'super_admin') {
    return true;
  }
  // Portal Admin (role 'admin') and any legacy non-super role see only the
  // explicit allowlist; this matches backend @RequireRoles guards.
  return Boolean(item.portalAdmin);
}

export function Sidebar({ currentPage, onPageChange, userRole }: SidebarProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() =>
    loadStringSet(COLLAPSED_GROUPS_STORAGE_KEY),
  );
  const [favoriteItems, setFavoriteItems] = useState<Set<string>>(() =>
    loadStringSet(FAVORITE_ITEMS_STORAGE_KEY),
  );

  useEffect(() => {
    persistStringSet(COLLAPSED_GROUPS_STORAGE_KEY, collapsedGroups);
  }, [collapsedGroups]);

  useEffect(() => {
    persistStringSet(FAVORITE_ITEMS_STORAGE_KEY, favoriteItems);
  }, [favoriteItems]);

  const toggleCollapsed = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const toggleFavoriteItem = (itemId: PageType) => {
    setFavoriteItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const visibleGroups = menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isVisible(item, userRole)),
    }))
    .filter((group) => group.items.length > 0);

  // Flatten visible items with their owning group's iconClass for the
  // Favoriten section — items keep the colour of their original group.
  const favoritedItems = visibleGroups.flatMap((group) =>
    group.items
      .filter((item) => favoriteItems.has(item.id))
      .map((item) => ({ item, iconColorClass: group.iconClass })),
  );

  const renderItem = (
    item: MenuItem,
    iconColorClass?: string,
    options?: { showFavoriteButton?: boolean; keyPrefix?: string },
  ) => {
    const Icon = item.icon;
    const isActive = currentPage === item.id;
    const isFavorite = favoriteItems.has(item.id);
    const showFavorite = options?.showFavoriteButton ?? true;
    const keyPrefix = options?.keyPrefix ?? '';

    return (
      <li key={`${keyPrefix}${item.id}`} className="relative group/item">
        <button
          onClick={() => onPageChange(item.id)}
          className={cn(
            'w-full flex items-center pl-3 pr-9 py-2 text-sm rounded-lg transition-colors duration-150 group',
            isActive
              ? 'bg-blue-50 text-blue-700 border border-blue-200'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
          )}
        >
          <Icon
            className={cn(
              'w-4 h-4 mr-3 flex-shrink-0',
              isActive
                ? 'text-blue-600'
                : iconColorClass ?? 'text-gray-400 group-hover:text-gray-500',
            )}
          />
          <div className="flex-1 text-left min-w-0">
            <div className="text-sm font-medium truncate">{item.label}</div>
            <div
              className={cn(
                'text-xs truncate',
                isActive ? 'text-blue-600' : 'text-gray-500',
              )}
            >
              {item.description}
            </div>
          </div>
        </button>
        {showFavorite && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleFavoriteItem(item.id);
            }}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity',
              isFavorite
                ? 'opacity-100 text-yellow-500 hover:text-yellow-600'
                : 'opacity-0 group-hover/item:opacity-100 text-gray-300 hover:text-gray-500',
            )}
            aria-label={
              isFavorite
                ? `${item.label} aus Favoriten entfernen`
                : `${item.label} zu Favoriten hinzufügen`
            }
            aria-pressed={isFavorite}
          >
            <Star
              className={cn(
                'w-3.5 h-3.5',
                isFavorite ? 'fill-yellow-500' : 'fill-none',
              )}
            />
          </button>
        )}
      </li>
    );
  };

  const renderGroup = (group: (typeof visibleGroups)[number]) => {
    const GroupIcon = group.icon;
    const isCollapsed = collapsedGroups.has(group.label);

    return (
      <div key={group.label} className="mb-4">
        <button
          type="button"
          onClick={() => toggleCollapsed(group.label)}
          className={cn(
            'w-full flex items-center px-3 mb-1.5 text-xs font-semibold uppercase tracking-wider text-left hover:opacity-80 transition-opacity',
            group.colorClass,
          )}
          aria-expanded={!isCollapsed}
          aria-label={`${group.label} ${isCollapsed ? 'aufklappen' : 'einklappen'}`}
        >
          <GroupIcon className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
          <span className="flex-1 truncate">{group.label}</span>
          <ChevronDown
            className={cn(
              'w-3.5 h-3.5 ml-1 flex-shrink-0 transition-transform duration-150',
              isCollapsed ? '-rotate-90' : 'rotate-0',
            )}
          />
        </button>
        {!isCollapsed && (
          <ul
            className={cn(
              'space-y-1 pl-2 border-l-2',
              group.accentClass,
            )}
          >
            {group.items.map((item) => renderItem(item, group.iconClass))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="w-72 bg-white shadow-sm border-r border-gray-200 flex flex-col">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">AI Platform</h1>
            <p className="text-xs text-gray-500">Administration</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-4 py-4 overflow-y-auto">
        {isVisible(overviewItem, userRole) && (
          <ul className="space-y-1 mb-4">{renderItem(overviewItem)}</ul>
        )}

        {favoritedItems.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center px-3 mb-1.5 text-xs font-semibold uppercase tracking-wider text-yellow-700">
              <Star className="w-3.5 h-3.5 mr-2 flex-shrink-0 fill-yellow-500 text-yellow-500" />
              Favoriten
            </div>
            <ul className="space-y-1 pl-2 border-l-2 border-yellow-200">
              {favoritedItems.map(({ item, iconColorClass }) =>
                renderItem(item, iconColorClass, { keyPrefix: 'fav-' }),
              )}
            </ul>
          </div>
        )}

        {visibleGroups.map((group) => renderGroup(group))}
      </nav>

      {isVisible(profileItem, userRole) && (
        <div className="px-4 py-3 border-t border-gray-200">
          <ul className="space-y-1">{renderItem(profileItem)}</ul>
        </div>
      )}
    </div>
  );
}
